<script setup>
import Layout from "../../../layouts/main.vue";
import { onMounted, ref, reactive, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { FormInstance } from "element-plus";
import MethodService from "../../../service/MethodService";
import DataForm from "./dataFormDynamicConfigPage";
import mushroom from "cem-primary-api";
import toastr from "toastr";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const ruleFormRef = ref(FormInstance);
const loadingBtn = ref(false);

const isUpdate = ref(false);
const dataForm = reactive(MethodService.copyObject(DataForm.dataForm));

// Danh sách tất cả bảng cấu hình động
const allConfigTables = ref([]);
// Select value để thêm bảng
const selectedTableId = ref("");

// Validation rules
const rules = reactive({
  name: [{ required: true, message: () => t("t-dynamic-config-page-validate-name-required"), trigger: "blur" }],
});

// Lấy danh sách tất cả bảng cấu hình động
const fn_LayDanhSachBangCauHinh = async () => {
  try {
    const response = await mushroom.dynamic_config_table.getAllAsync({
      fields: "id,name,name_lang",
    });
    if (response.result) {
      allConfigTables.value = response.result;
    }
  } catch (e) {
    MethodService.showError(e.code);
  }
};

// Lấy tên hiển thị bảng cấu hình
const getTableDisplayName = (tableOrId) => {
  const lang = localStorage.getItem("language") || "vi";
  let table = tableOrId;
  if (typeof tableOrId === "string") {
    table = allConfigTables.value.find((t) => t.id === tableOrId);
  }
  if (!table) return tableOrId;
  if (table.name_lang && table.name_lang[lang]) {
    return table.name_lang[lang];
  }
  return table.name || table.id;
};

// Danh sách bảng chưa được chọn
const availableTables = computed(() => {
  return allConfigTables.value.filter(
    (table) => !dataForm.table_ids.includes(table.id)
  );
});

// Thêm bảng vào danh sách
const fn_addTable = () => {
  if (!selectedTableId.value) return;
  dataForm.table_ids.push(selectedTableId.value);
  selectedTableId.value = "";
};

// Xóa bảng khỏi danh sách
const fn_removeTable = (index) => {
  dataForm.table_ids.splice(index, 1);
};

// Di chuyển bảng lên
const fn_moveTableUp = (index) => {
  if (index <= 0) return;
  const temp = dataForm.table_ids[index];
  dataForm.table_ids.splice(index, 1);
  dataForm.table_ids.splice(index - 1, 0, temp);
};

// Di chuyển bảng xuống
const fn_moveTableDown = (index) => {
  if (index >= dataForm.table_ids.length - 1) return;
  const temp = dataForm.table_ids[index];
  dataForm.table_ids.splice(index, 1);
  dataForm.table_ids.splice(index + 1, 0, temp);
};

// Thêm mới
const fn_ThemMoi = (formEl) => {
  loadingBtn.value = true;
  if (!formEl) return;

  formEl.validate(async (valid) => {
    if (valid) {
      const payload = buildPayload();
      try {
        const res = await mushroom.dynamic_config_page.createAsync(payload);
        if (res.result) {
          toastr.success(t("t-dynamic-config-page-add-success"));
          router.push({ name: "DanhSachDynamicConfigPage" });
        }
      } catch (e) {
        MethodService.showError(e.code);
      } finally {
        loadingBtn.value = false;
      }
    } else {
      loadingBtn.value = false;
      return false;
    }
  });
};

// Cập nhật
const fn_CapNhat = (formEl) => {
  loadingBtn.value = true;
  if (!formEl) return;

  formEl.validate(async (valid) => {
    if (valid) {
      const payload = buildPayload();
      payload.id = route.params.id;
      try {
        const res = await mushroom.dynamic_config_page.partialUpdateAsync(payload);
        if (res.result) {
          toastr.success(t("t-dynamic-config-page-update-success"));
          router.push({ name: "DanhSachDynamicConfigPage" });
        }
      } catch (e) {
        MethodService.showError(e.code);
      } finally {
        loadingBtn.value = false;
      }
    } else {
      loadingBtn.value = false;
      return false;
    }
  });
};

// Build payload
const buildPayload = () => {
  return {
    name: dataForm.name,
    name_lang: dataForm.name_lang,
    table_ids: dataForm.table_ids,
  };
};

// Lấy chi tiết khi cập nhật
const fn_LayChiTiet = async () => {
  try {
    const res = await mushroom.dynamic_config_page.findByIdAsync({
      id: route.params.id,
    });
    if (res.result) {
      const data = res.result;
      dataForm.name = data.name;
      dataForm.name_lang = data.name_lang || { vi: "", en: "" };
      dataForm.table_ids = data.table_ids || [];
    }
  } catch (e) {
    MethodService.showError(e.code);
  }
};

// Quay lại
const fn_QuayLai = () => {
  router.push({ name: "DanhSachDynamicConfigPage" });
};

onMounted(async () => {
  await fn_LayDanhSachBangCauHinh();
  if (route.name === "CapNhatDynamicConfigPage") {
    isUpdate.value = true;
    await fn_LayChiTiet();
  }
});
</script>

<template>
  <Layout>
    <el-form
      ref="ruleFormRef"
      :model="dataForm"
      :rules="rules"
      label-position="top"
      @submit.prevent
    >
      <div class="card">
        <div class="card-header">
          <h3 class="mb-0">{{ $t("t-dynamic-config-page") }}</h3>
        </div>
        <div class="card-body">
          <!-- Tên -->
          <b-row>
            <b-col md="6">
              <el-form-item prop="name">
                <template #label>{{ $t('t-dynamic-config-page-name') }}</template>
                <el-input v-model="dataForm.name" :placeholder="$t('t-dynamic-config-page-name-placeholder')" size="large"></el-input>
              </el-form-item>
            </b-col>
            <b-col md="3">
              <el-form-item :label="$t('t-dynamic-config-page-name-vi')">
                <el-input v-model="dataForm.name_lang.vi" :placeholder="$t('t-dynamic-config-page-name-vi-placeholder')" size="large"></el-input>
              </el-form-item>
            </b-col>
            <b-col md="3">
              <el-form-item :label="$t('t-dynamic-config-page-name-en')">
                <el-input v-model="dataForm.name_lang.en" :placeholder="$t('t-dynamic-config-page-name-en-placeholder')" size="large"></el-input>
              </el-form-item>
            </b-col>
          </b-row>

          <!-- Chọn bảng cấu hình -->
          <b-row class="mt-3">
            <b-col md="12">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <label class="el-form-item__label mb-0" style="white-space: nowrap;text-transform: uppercase; margin-right: 12px;">{{ $t('t-dynamic-config-page-tables') }}</label>
                <div class="d-flex gap-2 align-items-center" style="flex: 1;">
                  <el-select
                    v-model="selectedTableId"
                    :placeholder="$t('t-dynamic-config-page-select-table')"
                    size="large"
                    style="width: 100%"
                    filterable
                    clearable
                  >
                    <el-option
                      v-for="table in availableTables"
                      :key="table.id"
                      :label="getTableDisplayName(table)"
                      :value="table.id"
                    ></el-option>
                  </el-select>
                  <button type="button" style="height: 40px" class="btn btn-soft-success" @click="fn_addTable" :disabled="!selectedTableId">
                    <i class="ri-add-line"></i> {{ $t("t-dynamic-config-page-add-table") }}
                  </button>
                </div>
              </div>

              <!-- Danh sách đã chọn -->
              <div v-if="dataForm.table_ids.length > 0">
                <div
                  v-for="(tableId, index) in dataForm.table_ids"
                  :key="tableId"
                  class="d-flex align-items-center justify-content-between border rounded p-2 mb-2"
                >
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-soft-secondary text-dark">{{ index + 1 }}</span>
                    <span>{{ getTableDisplayName(tableId) }}</span>
                  </div>
                  <div class="d-flex gap-1">
                    <button type="button" class="btn btn-soft-secondary btn-sm" @click="fn_moveTableUp(index)" :disabled="index === 0">
                      <i class="ri-arrow-up-line"></i>
                    </button>
                    <button type="button" class="btn btn-soft-secondary btn-sm" @click="fn_moveTableDown(index)" :disabled="index === dataForm.table_ids.length - 1">
                      <i class="ri-arrow-down-line"></i>
                    </button>
                    <button type="button" class="btn btn-soft-danger btn-sm" @click="fn_removeTable(index)">
                      <i class="ri-close-line"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="text-muted text-center py-3">
                {{ $t("t-dynamic-config-page-no-tables") }}
              </div>
            </b-col>
          </b-row>
        </div>
      </div>

      <div class="button-group">
        <button type="button" class="btn btn-soft-secondary btn-border me-2" @click="fn_QuayLai">
          {{ $t("t-back") }}
        </button>
        <button v-if="!isUpdate" type="button" class="btn btn-secondary btn-border" :disabled="loadingBtn" @click="fn_ThemMoi(ruleFormRef)">
          <i class="ri-save-line me-1"></i> {{ $t("t-add") }}
        </button>
        <button v-else type="button" class="btn btn-secondary btn-border" :disabled="loadingBtn" @click="fn_CapNhat(ruleFormRef)">
          <i class="ri-save-line me-1"></i> {{ $t("t-update") }}
        </button>
      </div>
    </el-form>
  </Layout>
</template>

<style scoped lang="scss">
.button-group {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}
.btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
